<script setup>
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import {reactive, ref, watch} from "vue";
import {getLogo} from "@/helpers/logo";
import useForm from "@/composables/useForm";
import trackersCompaniesService from "@/services/trackers-companies.service";
import {decodeIntervalFromServer, encodeIntervalForServer} from "@/helpers/date";
import DatePicker from "vue2-datepicker";

const {trackerId, permissions} = defineProps(['trackerId', 'permissions']);
const emit = defineEmits(['removeStartup', 'timelineChanged']);

const form = reactive({
    note: null,
    status: null,
    department: null,
    sourcedBy: null,
    startDate: null,
    endDate: null,
    testStartDate: null,
    testEndDate: null,
    subPeriods: [],
})

const {resetForm, populateForm} = useForm(form);

const showModal = ref(false);
const startupId = ref(null);
const details = ref({});
const errors = ref({});
const loading = ref(false);

const povTimeline = ref([null, null]);
const testTimeline = ref([null, null]);
const subPeriods = ref([]);

watch(povTimeline, () => {
    if (povTimeline.value[0]) {
        let interval = encodeIntervalForServer(povTimeline.value);

        form.startDate = interval[0];
        form.endDate = interval[1];
    } else {
        form.startDate = null;
        form.endDate = null;
    }
})

watch(testTimeline, () => {
    if (testTimeline.value[0]) {
        let interval = encodeIntervalForServer(testTimeline.value);

        form.testStartDate = interval[0];
        form.testEndDate = interval[1];
    } else {
        form.testStartDate = null;
        form.testEndDate = null;
    }
})

watch(subPeriods, (values) => {
    values.forEach((period, index) => {
        if (period.value[0]) {
            form.subPeriods[index].value = encodeIntervalForServer(period.value);
        } else {
            form.subPeriods[index].value = null;
        }
    });
}, {deep: true});

function init() {
    resetForm();
    details.value = {};
    errors.value = {};
    povTimeline.value = [null, null];
    testTimeline.value = [null, null];
    subPeriods.value = [];

    getData();
}

async function getData() {
    const {data} = await trackersCompaniesService.getTimelineDetails(trackerId, startupId.value);
    details.value = data;

    populateForm(details.value);

    if (details.value.start_date && details.value.end_date) {
        povTimeline.value = decodeIntervalFromServer([details.value.start_date, details.value.end_date]);
    }

    if (details.value.test_start_date && details.value.test_end_date) {
        testTimeline.value = decodeIntervalFromServer([details.value.test_start_date, details.value.test_end_date]);
    }

    details.value.sub_periods.forEach((subPeriod) => {
        let period = [null, null];

        if (subPeriod.value) {
            period = decodeIntervalFromServer([subPeriod.value[0], subPeriod.value[1]]);
        }

        subPeriods.value.push({
            id: subPeriod.id,
            name: subPeriod.name,
            value: period
        });
    });
}

function open(id) {
    startupId.value = id;
    showModal.value = true;
    init();
}

async function save() {
    loading.value = true;
    const {data} = await trackersCompaniesService.saveTimelineDetails(trackerId, startupId.value, form);

    if (Object.keys(data.errors).length) {
        errors.value = data.errors;
    } else {
        showModal.value = false;
        emit('timelineChanged');
    }

    loading.value = false;
}

function removeStartup() {
    if (confirm('Remove Startup?')) {
        emit('removeStartup', startupId.value);
        showModal.value = false;
    }
}

function disabledDate(date) {
    return !povTimeline.value[0] || date < povTimeline.value[0] || date > povTimeline.value[1];
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" modal-class="modal-w-xxl modal-funnel-card-popup modal-funnel-card-popup--columns">
        <form class="form">
            <div class="modal-funnel-card-popup--columns__content">
                <b-col>
                    <div class="modal-funnel-card-popup--columns__col">
                        <template v-if="details">
                            <div class="d-flex align-items-center mb-3">
                                <div class="image-block">
                                    <img :src="getLogo(details.startup_logo)" alt="">
                                </div>
                                <h3 class="heading3 mb-0">
                                    <router-link :to="'/startups/' + startupId">{{details.startup_name}}</router-link>
                                </h3>
                            </div>
                        </template>

                        <p>{{details.startup_description}}</p>

                        <h5 class="heading5 heading5--secondary">Department</h5>

                        <b-form-input
                            :disabled="!permissions.includes('tracker-diligenceFields-value-edit')"
                            class="form-control--mid mb-3 w-50"
                            v-model="form.department"
                        />

                        <!--  -->

                        <h5 class="heading5 heading5--secondary">Sourced By</h5>

                        <b-form-input
                            :disabled="!permissions.includes('tracker-diligenceFields-value-edit')"
                            class="form-control--mid mb-3 w-50"
                            v-model="form.sourcedBy"
                        />

                        <!--  -->

                        <h5 class="heading5 heading5--secondary">Notes</h5>

                        <b-form-textarea
                            :disabled="!permissions.includes('tracker-diligenceFields-value-edit')"
                            class="form-control--mid mb-3"
                            v-model="form.note"
                        />
                    </div>
                </b-col>
                <b-col>
                    <div class="modal-funnel-card-popup--columns__col">
                        <h5 class="heading5 heading5--secondary">Status</h5>

                        <div class="mb-3">
                            <b-form-select
                                v-model="form.status"
                                :disabled="!permissions.includes('tracker-change-status')" class="w-50"
                            >
                                <option v-for="status in details.statuses" :key="status.id" :value="status.id">
                                    {{status.name}}
                                </option>
                            </b-form-select>
                            <div v-if="'status' in errors" class="form__error" >{{errors.status}}</div>
                        </div>

                        <div class="modal-funnel-card-popup--inrow">
                            <div class="input-holder">
                                <h5 class="heading5 heading5--secondary">POC Dates</h5>

                                <span v-if="'testStartDate' in errors" class="form__error">{{errors.testStartDate}}</span>

                                <DatePicker
                                    :disabled="!permissions.includes('tracker-diligenceFields-value-edit')"
                                    v-model="povTimeline"
                                    format = "MM/DD/YYYY"
                                    placeholder="Select Start / End Date"
                                    range class=""
                                />
                            </div>

                            <div class="input-holder">
                                <h5 class="heading5 heading5--secondary">Testing Dates</h5>

                                <DatePicker
                                    :disabled="!permissions.includes('tracker-diligenceFields-value-edit') || !povTimeline[0]"
                                    v-model="testTimeline"
                                    :disabled-date="disabledDate"
                                    :default-value="povTimeline"
                                    format = "MM/DD/YYYY"
                                    placeholder="Select Start / End Date"
                                    range class=""
                                />
                            </div>
                        </div>

                        <template v-if="subPeriods.length">
                            <h5 class="heading5 heading5--secondary">Sub-Dates</h5>

                            <div class="modal-funnel-card-popup--subsection">
                                <b-row>
                                    <b-col>
                                        <b-row v-for="(period, index) in subPeriods" :key="period.id">
                                            <b-col>
                                                <h6 class="heading5 heading5--secondary">{{period.name}}</h6>

                                                <span v-if="typeof errors.subPeriods !== 'undefined' && typeof errors.subPeriods[index] !== 'undefined'" class="form__error" >{{errors.subPeriods[index]}}</span>

                                                <DatePicker
                                                    :disabled="!permissions.includes('tracker-diligenceFields-value-edit') || !povTimeline[0]"
                                                    v-model="subPeriods[index].value"
                                                    :disabled-date="disabledDate"
                                                    :default-value="povTimeline"
                                                    format = "MM/DD/YYYY"
                                                    placeholder="Select Start / End Date"
                                                    range class="mb-3 w-75"
                                                />
                                            </b-col>
                                        </b-row>
                                    </b-col>
                                </b-row>
                            </div>
                        </template>
                    </div>
                </b-col>
            </div>
        </form>

        <template #modal-footer>
            <template v-if="details">
                <a @click="removeStartup" class="btn btn-red ml-auto text-nowrap">Remove From Tracker</a>
            </template>

            <primary-button
                :loading="loading"
                caption="Save"
                loading-caption="Saving..."
                @click="save"
            />
        </template>

    </b-modal>
</template>

<style scoped>

</style>
